import { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, Plus } from "lucide-react";
import { getStatusLabel, displayName, autoName } from "./helpers";
import useInlineEdit from "./useInlineEdit";

// Status → dot color for the quick-switch list. Mirrors the lifecycle buckets
// the rest of the workspace uses (attention = needs a decision / reply).
function switcherStatusClass(s) {
	if (s.status === "BOOKED") return "is-booked";
	if (s.status === "DECIDE" || s.status === "NEEDS_REPLY") return "is-attention";
	if (s.status === "RFQS_SENT") return "is-sent";
	return "is-draft";
}

// Breadcrumb quick-switch popover — replaces the cryptic mini-rail. Lists every
// shipment with a readable name, its route + mode, and a status dot; switching
// is one click and costs zero permanent horizontal space. "View all" goes to
// the shipments grid; "New shipment" preserves the + the mini-rail used to host.
function ShipmentSwitcher({
	shipments,
	activeId,
	onSelect,
	onNew,
	backTo,
	onClose,
	anchorRef,
}) {
	const popRef = useRef(null);
	useEffect(() => {
		const onDown = (e) => {
			if (
				popRef.current &&
				!popRef.current.contains(e.target) &&
				anchorRef.current &&
				!anchorRef.current.contains(e.target)
			)
				onClose();
		};
		const onKey = (e) => {
			if (e.key === "Escape") onClose();
		};
		document.addEventListener("mousedown", onDown);
		document.addEventListener("keydown", onKey);
		return () => {
			document.removeEventListener("mousedown", onDown);
			document.removeEventListener("keydown", onKey);
		};
	}, [onClose, anchorRef]);

	return (
		<div className="ws-switcher" ref={popRef} role="menu">
			<div className="ws-switcher-head">
				<span className="ws-switcher-title">Recent shipments</span>
				{backTo && (
					<Link to={backTo} className="ws-switcher-viewall" onClick={onClose}>
						View all
					</Link>
				)}
			</div>
			<div className="ws-switcher-list">
				{shipments.map((s) => {
					const o = s.route?.origin?.name || s.route?.origin?.code || "—";
					const d =
						s.route?.destination?.name || s.route?.destination?.code || "—";
					const modeSuffix = s.mode && s.mode !== "—" ? ` · ${s.mode}` : "";
					return (
						<button
							key={s.id}
							type="button"
							role="menuitem"
							className={`ws-switcher-item ${s.id === activeId ? "is-active" : ""}`}
							onClick={() => {
								onSelect?.(s.id);
								onClose();
							}}
						>
							<span className="ws-switcher-item-row">
								<span className="ws-switcher-name">
									{displayName(s, shipments)}
								</span>
								<span className="ws-switcher-status">
									<span className={`ws-switcher-dot ${switcherStatusClass(s)}`} />
									{getStatusLabel(s)}
								</span>
							</span>
							<span className="ws-switcher-sub">
								{o} → {d}
								{modeSuffix}
							</span>
						</button>
					);
				})}
			</div>
			{onNew && (
				<button
					type="button"
					className="ws-switcher-new"
					onClick={() => {
						onNew();
						onClose();
					}}
				>
					<Plus size={14} strokeWidth={2.2} /> New shipment
				</button>
			)}
		</div>
	);
}

export default function Header({
	shipment,
	allShipments,
	onReplay,
	replaying,
	onRename,
	// When set (preview only), render a breadcrumb back to the shipments list.
	// Production (V2WorkspacePage) omits it, so its header is unchanged.
	backTo,
	// Preview-only quick-switch wiring. When onSelect is provided, the current
	// crumb becomes a dropdown that switches shipments in place.
	onSelect,
	onNew,
	activeId,
}) {
	const [switcherOpen, setSwitcherOpen] = useState(false);
	const anchorRef = useRef(null);
	const canSwitch = typeof onSelect === "function";
	const name = displayName(shipment, allShipments);
	const route = `${shipment.route.origin.code} → ${shipment.route.destination.code}`;
	const cargoLabel = shipment.cargo.palletCount
		? `${shipment.cargo.palletCount} plt · ${(shipment.cargo.totalWeight || 0).toLocaleString()} lbs`
		: "";
	// Same shared edit hook the sidebar + rail use. Click the title to rename.
	const edit = useInlineEdit({
		value: shipment.userName || autoName(shipment),
		onCommit: (val) => {
			const trimmed = (val || "").trim();
			const auto = autoName(shipment);
			// Treat "no change vs auto-name" as a revert: persisting the auto-name
			// string would freeze it (later origin/destination updates couldn't
			// refresh the title). null lets displayName() keep deriving live.
			const effective =
				trimmed.length === 0 || trimmed === auto ? null : trimmed;
			onRename?.(shipment.id, effective);
		},
	});
	return (
		<div className="ws-header">
			{backTo && (
				<nav className="ws-header-crumbs" aria-label="Breadcrumb">
					<Link to={backTo} className="ws-header-crumb-link">
						Shipments
					</Link>
					<span className="ws-header-crumb-sep">›</span>
					{canSwitch ? (
						<span className="ws-switcher-anchor">
							<button
								type="button"
								ref={anchorRef}
								className="ws-header-crumb-current ws-header-crumb-switch"
								onClick={() => setSwitcherOpen((o) => !o)}
								aria-haspopup="menu"
								aria-expanded={switcherOpen}
							>
								{name}
								<ChevronDown
									size={13}
									strokeWidth={2}
									className="ws-header-crumb-chev"
								/>
							</button>
							{switcherOpen && (
								<ShipmentSwitcher
									shipments={allShipments || []}
									activeId={activeId}
									onSelect={onSelect}
									onNew={onNew}
									backTo={backTo}
									anchorRef={anchorRef}
									onClose={() => setSwitcherOpen(false)}
								/>
							)}
						</span>
					) : (
						<span className="ws-header-crumb-current">{name}</span>
					)}
				</nav>
			)}
			<div className="ws-header-row1">
				{edit.editing ? (
					<input
						ref={edit.inputRef}
						className="ws-header-route-input"
						value={edit.draft}
						onChange={(e) => edit.setDraft(e.target.value)}
						onBlur={edit.attemptSave}
						onKeyDown={edit.handleKey}
					/>
				) : (
					<div
						className="ws-header-route ws-header-route--editable"
						title="Click to rename"
						onClick={() => onRename && edit.begin()}
					>
						{name}
					</div>
				)}
				<span className={`ws-status-pill ws-status-${shipment.status}`}>
					{getStatusLabel(shipment)}
				</span>
			</div>
			<div className="ws-header-row2">
				<span>{route}</span>
				<span className="sep">·</span>
				<span>{shipment.mode}</span>
				{cargoLabel && (
					<>
						<span className="sep">·</span>
						<span>{cargoLabel}</span>
					</>
				)}
				<span className="sep">·</span>
				<span>
					Created {new Date(shipment.createdAt).toLocaleDateString()}
					{shipment.createdBy ? ` by ${shipment.createdBy}` : ""}
				</span>
			</div>
		</div>
	);
}

export function NewShipmentFormBar({ draft, onChange, onStart }) {
	return (
		<div className="ws-form-bar">
			<div className="ws-form-field">
				<span className="ws-form-label">Origin</span>
				<input
					className="ws-form-input"
					placeholder="City, port, or airport"
					value={draft.origin}
					onChange={(e) => onChange({ ...draft, origin: e.target.value })}
				/>
			</div>
			<div className="ws-form-field">
				<span className="ws-form-label">Destination</span>
				<input
					className="ws-form-input"
					placeholder="City, port, or airport"
					value={draft.destination}
					onChange={(e) => onChange({ ...draft, destination: e.target.value })}
				/>
			</div>
			<div className="ws-form-field">
				<span className="ws-form-label">Mode</span>
				<select
					className="ws-form-input"
					value={draft.mode}
					onChange={(e) => onChange({ ...draft, mode: e.target.value })}
				>
					<option value="">—</option>
					<option>Ocean FCL</option>
					<option>Ocean LCL</option>
					<option>Air</option>
					<option>Truck FTL</option>
					<option>Truck LTL</option>
				</select>
			</div>
			<div className="ws-form-field">
				<span className="ws-form-label">Ready by</span>
				<input
					type="date"
					className="ws-form-input"
					value={draft.readyDate}
					onChange={(e) => onChange({ ...draft, readyDate: e.target.value })}
				/>
			</div>
			<button
				className="ws-btn ws-btn--primary"
				onClick={onStart}
				disabled={!draft.origin || !draft.destination || !draft.mode}
			>
				Start
			</button>
		</div>
	);
}
